/**
 * `size_width` en `size_height` (en de generieke `width`/`height`) staan nooit
 * in het optieschema van een product (`Product.options`): ze ontstaan pas
 * zodra een klant zelf in het "Eigen formaat"-veld typt (zie
 * `GroupedSizeSelector` / `onCustomSizeChange` in product-configurator.tsx).
 * Ze zijn dus "virtueel" - wel een geldig, opgeslagen deel van een
 * bestelling, maar geen optie die de configurator uit zijn eigen schema kent.
 */
export const VIRTUAL_SIZE_DERIVED_KEYS = new Set(['size', 'width', 'height', 'size_width', 'size_height']);

/**
 * De init-`useEffect` van de configurator bouwt zijn standaardconfiguratie
 * door alleen `product.options` (het schema) te doorlopen en per optie te
 * kijken of `initialConfiguration` al een waarde heeft. Omdat de virtuele
 * maatvelden niet in dat schema staan, worden ze door die stap overgeslagen -
 * ook als ze wel degelijk in `initialConfiguration` zitten (bijvoorbeeld
 * wanneer een medewerker in de admin een bestaande order met een vrije maat
 * heropent). Zonder deze aanvulling blijft `size_width`/`size_height`
 * `undefined` en kan `formatSizeForEditor()` geen geldig formaat bouwen, met
 * als gevolg dat de ontwerpeditor terugvalt op de kale string `"custom"`.
 *
 * Geeft alleen de sleutels terug die nog ontbreken in `defaultConfig` (dus
 * nooit een schema-gedreven standaardwaarde overschrijven) en wel aanwezig
 * zijn in `initialConfiguration`.
 */
export function getVirtualSizeConfigFromInitial(
  defaultConfig: Record<string, string | number>,
  initialConfiguration: Record<string, string | number> | null | undefined,
): Record<string, string | number> {
  if (!initialConfiguration) return {};

  const additions: Record<string, string | number> = {};
  for (const key of VIRTUAL_SIZE_DERIVED_KEYS) {
    if (defaultConfig[key] !== undefined) continue;
    const value = initialConfiguration[key];
    if (value === undefined) continue;
    additions[key] = value;
  }
  return additions;
}
